import { EBrowser, ERequestState } from '@NGUtils/enums';
import { EStrapiRichText } from '@components/common/StrapiRichText/StrapiRichText.enum';
import { IStrapiRichText } from '@components/common/StrapiRichText/StrapiRichText.type';
import { ISubmenu } from '@components/navbar/Sublinks/Sublinks.type';
import { ICampaign } from '@devHub/interfaces/devhub';
import { EOriginQuest } from '@store/appState/appState.enum';
import { INetwork } from '@store/web3Store/web3Store.type';
import { NETWORKS_DATA } from '@store/web3Store/web3Store.variables';
import { APP_LINKS, APP_LINKS_ARRAY, APP_SUB_MENUS, LANGUAGE_REGEX, URL_PARAMS } from '@utils/variables';
import katex from 'katex';
import router from 'next/router';
import pluralize from 'pluralize';
import { IAppLink } from '../types/global';
import { updateContentTag } from './getHtmlTags';

function removeDuplicates<TArray>(arr: TArray[], uniqueProperty: keyof TArray): TArray[] {
	return arr.filter(
		(obj, index, self) =>
			index ===
			self.findIndex((t) => {
				return t[uniqueProperty] === obj[uniqueProperty];
			})
	);
}

function naturalSort(as: string | number, bs: string | number) {
	if (typeof as === 'number' && typeof bs === 'number') {
		return as > bs ? 1 : -1;
	}

	const rx = /(\.\d+)|(\d+(\.\d+)?)|([^\d.]+)|(\.\D+)|(\.$)/g;
	const a = as.toString().toLowerCase().match(rx) || [];
	const b = bs.toString().toLowerCase().match(rx) || [];

	for (const [aPart, bPart] of zip(a, b)) {
		if (aPart === undefined) {
			return 1;
		} else if (bPart === undefined) {
			return -1;
		} else if (isNumeric(aPart) && isNumeric(bPart)) {
			const diff = Number(aPart) - Number(bPart);
			if (!Number.isNaN(diff)) {
				return Math.sign(diff);
			}
		} else {
			const diff = aPart.localeCompare(bPart);
			if (diff !== 0) {
				return diff;
			}
		}
	}

	return 0;
}

function* zip<T>(a: T[], b: T[]): IterableIterator<[T | undefined, T | undefined]> {
	const length = Math.max(a.length, b.length);
	for (let i = 0; i < length; i++) {
		yield [a[i], b[i]];
	}
}

function isNumeric(value: string): boolean {
	return /^-?\d+(\.\d+)?$/.test(value);
}

function capitalize(str: string | undefined): string {
	if (!str) {
		return '';
	}

	return str.charAt(0).toUpperCase() + str.slice(1);
}

// sonar-ignore-start

function buildURL(
	pathname: string,
	subpage: string,
	selectedCampaign?: string,
	selectedQuest?: string,
	selectedFaq?: string
): string {
	let newQuery: { [key: string]: string } = {};

	if (!pathname) {
		return '#';
	}

	if (!subpage) {
		return pathname;
	}

	if (subpage) {
		newQuery = {
			...newQuery,
			[URL_PARAMS.subpage]: subpage
		};
	}

	if (selectedCampaign) {
		newQuery = {
			...newQuery,
			[URL_PARAMS.selectedCampaign]: selectedCampaign
		};
	}

	if (selectedQuest) {
		newQuery = {
			...newQuery,
			[URL_PARAMS.selectedQuest]: selectedQuest
		};
	}

	if (selectedFaq) {
		newQuery = {
			...newQuery,
			[URL_PARAMS.selectedFaq]: selectedFaq
		};
	}

	const params = new URLSearchParams(newQuery).toString();

	return `${pathname}?${params}`;
}

/**
 * Format router object to push a new selected campaign or quest
 * @param pathname base page path (ex: /dev-hub)
 * @param subpage subpage (ex: devhub-campaign)
 * @param selectedCampaign selected campaign (campaign slug)
 * @param selectedQuest selected quest (quest slug)
 * @returns
 */
function pushURL(
	pathname: string,
	subpage: string,
	selectedCampaign?: string,
	selectedQuest?: string,
	selectedFaq?: string
) {
	let newQuery: { [key: string]: string } = {};

	// used to prettier the url
	let asValue: string | undefined = undefined;

	if (!pathname) return;

	// asValue = pathname;

	if (subpage) {
		newQuery = {
			...newQuery,
			[URL_PARAMS.subpage]: subpage
		};

		// remove all content of subpage before - (ex: devhub-campaign => campaign)
		// asValue += `/${subpage.split('-')[1]}`;
	}

	if (selectedCampaign) {
		newQuery = {
			...newQuery,
			[URL_PARAMS.selectedCampaign]: selectedCampaign
		};
		// asValue += `/${selectedCampaign}`;
	} else {
		// @ts-ignore
		delete window[URL_PARAMS.selectedCampaign];
	}

	if (selectedQuest) {
		newQuery = {
			...newQuery,
			[URL_PARAMS.selectedQuest]: selectedQuest
		};
		// asValue += `/${selectedQuest}`;
	} else {
		// @ts-ignore
		delete window[URL_PARAMS.selectedQuest];
	}

	if (selectedFaq) {
		newQuery = {
			...newQuery,
			[URL_PARAMS.selectedFaq]: selectedFaq
		};
		// asValue += `/${selectedFaq}`;
	} else {
		// @ts-ignore
		delete window[URL_PARAMS.selectedFaq];
	}

	/* 	if (JSON.stringify(newQuery) === JSON.stringify(router.query) && !router.query?.r) {
		newQuery = {
			...newQuery,
			r: '1'
		};
	} */

	router.push(
		{
			pathname,
			query: newQuery
		},
		asValue,
		{ shallow: true }
	);
}
// sonar-ignore-end

/**
 * push a new url to go back to devhub to a specific campaign or quest
 * @param originQuest origin of the quest (from dev hub campaign or from dev hub quest)
 * @param questSlug quest slug
 * @param campaignSlug campaign slug
 */
function goBackToDevhub(originQuest: EOriginQuest, questSlug?: string, campaignSlug?: string) {
	if (!questSlug) return;

	if (originQuest === EOriginQuest.fromCampaign) {
		pushURL(APP_LINKS.devhub.href, APP_SUB_MENUS.devhub.campaigns.id, campaignSlug);
	} else {
		pushURL(APP_LINKS.devhub.href, APP_SUB_MENUS.devhub.quests.id, undefined, questSlug);
	}
}

// push internal url
function pushInternalURL(url: string) {
	router.push(url, undefined, { shallow: true });
}

// remove a query parametere from the url using next/router
function removeQueryParams(paramToRemove: string) {
	const { pathname, query } = router;

	const newQuery = { ...query };
	delete newQuery[paramToRemove];

	router.push(
		{
			pathname,
			query: newQuery
		},
		undefined,
		{ shallow: true }
	);
}

// update query params using next/router
function updateQueryParams(newQuery: { [key: string]: string }) {
	const { pathname, query } = router;

	router.push(
		{
			pathname,
			query: {
				...query,
				...newQuery
			}
		},
		undefined,
		{ shallow: true }
	);
}

// convert a stringified object to an object
function jsonToObject(str: string | undefined) {
	if (!str) {
		return {};
	}

	try {
		return JSON.parse(str);
	} catch {
		return {};
	}
}

// get coords in document of an element
function getCoords(elem: Element) {
	// crossbrowser version
	var box = elem.getBoundingClientRect();

	var body = document.body;
	var docEl = document.documentElement;

	var scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
	var scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

	var clientTop = docEl.clientTop || body.clientTop || 0;
	var clientLeft = docEl.clientLeft || body.clientLeft || 0;

	var top = box.top + scrollTop - clientTop;
	var left = box.left + scrollLeft - clientLeft;

	return { top: Math.round(top), left: Math.round(left) };
}

function formatRichText(blocks: IStrapiRichText[]): IStrapiRichText[] {
	const newBlocks = [...blocks];

	// run through all newBlocks and check if item with type is image and a caption equal to  an another item with type quote and a caption
	newBlocks.forEach((block, index) => {
		if (!!block.data.text) {
			block.data.text = updateContentTag(block.data.text);
			block.contentTagUpdated = true;
		}

		if (!!block.data.items) {
			block.data.items = block.data.items.map((item) => updateContentTag(item));
			block.contentTagUpdated = true;
		}

		if (!!block.data.content) {
			block.data.content = block.data.content.map((row) => row.map((cell) => updateContentTag(cell)));
			block.contentTagUpdated = true;
		}

		if (block.type === EStrapiRichText.image) {
			const nextBlock = newBlocks[index + 1];

			if (nextBlock?.type === EStrapiRichText.quote && nextBlock?.data?.caption === block?.data?.caption) {
				//block.hasQuote = true;
				nextBlock.data.img = block?.data?.file?.formats?.thumbnail?.url || block?.data?.file?.url || '';

				// remove block from array
				newBlocks.splice(index, 1);
			}
		} else if (block.type === EStrapiRichText.paragraph) {
			// array of new texts
			const newTexts: string[] = [];

			if (!!block.data?.text?.includes('#lang:')) {
				let splitStr = '<code class="richtext rt-inlinecode">';

				block.data.text.split(splitStr).forEach((text) => {
					if (text.match(LANGUAGE_REGEX)) {
						// split by </code>
						const splitText = text.split('</code>');

						const html = katex.renderToString(splitText[0].replace(LANGUAGE_REGEX, ''), {
							displayMode: false,
							throwOnError: false,
							maxExpand: 1000, // prevent infinite loop
							trust: false,
							output: 'mathml'
						});

						// push math
						newTexts.push(html);

						// push rest of text
						newTexts.push(splitText[1]);
					} else if (text.includes('</code>')) {
						newTexts.push(splitStr + text);
					} else {
						newTexts.push(text);
					}
				});

				// replace text with new text
				block.data.text = newTexts.join('');
			}
		} else if (block.type === EStrapiRichText.list) {
			block.data.items ||= [];

			// do the same for list with items
			block.data.items = block.data.items.map((item) => {
				// array of new texts
				const newTexts: string[] = [];

				if (!!item.includes('#lang:')) {
					let splitStr = '<code class="richtext rt-inlinecode">';

					item.split(splitStr).forEach((text) => {
						if (text.match(LANGUAGE_REGEX)) {
							// split by </code>
							const splitText = text.split('</code>');

							const html = katex.renderToString(splitText[0].replace(LANGUAGE_REGEX, ''), {
								displayMode: false,
								throwOnError: false,
								maxExpand: 1000, // prevent infinite loop
								trust: false,
								output: 'mathml'
							});

							// push math
							newTexts.push(html);

							// push rest of text
							newTexts.push(splitText[1]);
						} else if (text.includes('</code>')) {
							newTexts.push(splitStr + text);
						} else {
							newTexts.push(text);
						}
					});

					// replace text with new text
					return newTexts.join('');
				}

				return item;
			});
		} else if (block.type === EStrapiRichText.table) {
			block.data.content ||= [];

			// do the same for list with items
			block.data.content = block.data.content.map((row) => {
				return row.map((cell) => {
					// array of new texts
					const newTexts: string[] = [];

					if (!!cell.includes('#lang:')) {
						let splitStr = '<code class="richtext rt-inlinecode">';

						cell.split(splitStr).forEach((text) => {
							if (text.match(LANGUAGE_REGEX)) {
								// split by </code>
								const splitText = text.split('</code>');

								const html = katex.renderToString(splitText[0].replace(LANGUAGE_REGEX, ''), {
									displayMode: false,
									throwOnError: false,
									maxExpand: 1000, // prevent infinite loop
									trust: false,
									output: 'mathml'
								});

								// push math
								newTexts.push(html);

								// push rest of text
								newTexts.push(splitText[1]);
							} else if (text.includes('</code>')) {
								newTexts.push(splitStr + text);
							} else {
								newTexts.push(text);
							}
						});

						// replace text with new text
						return newTexts.join('');
					}

					return cell;
				});
			});
		}
	});

	return newBlocks;
}

function getBrowser(): EBrowser {
	if (typeof window === 'undefined' || typeof navigator === 'undefined') {
		return EBrowser.server;
	}
	let browser: EBrowser;
	let c = navigator.userAgent.search('Chrome');
	if (c < 0) {
		// chrome on iOS
		c = navigator.userAgent.search('CriOS');
	}
	const f = navigator.userAgent.search('Firefox');
	const m8 = navigator.userAgent.search('MSIE 8.0');
	const m9 = navigator.userAgent.search('MSIE 9.0');
	const m10 = navigator.userAgent.search('MSIE 10.0');
	const m11 = navigator.userAgent.search('MSIE 11.0');
	const o = navigator.userAgent.search('Opera');
	const s = navigator.userAgent.search('Safari');
	const brave = typeof navigator.brave !== 'undefined';

	if (brave) {
		browser = EBrowser.brave;
	} else if (c > -1) {
		browser = EBrowser.chrome;
	} else if (f > -1) {
		browser = EBrowser.firefox;
	} else if (m8 > -1 || m9 > -1 || m10 > -1 || m11 > -1) {
		browser = EBrowser.ie;
	} else if (o > -1) {
		browser = EBrowser.opera;
	} else if (s > -1) {
		browser = EBrowser.safari;
	} else {
		browser = EBrowser.other;
	}

	return browser;
}

function checkIfError<T>(response: T | TServerErrorResponse): { error: boolean; message: string } {
	const typedResponse = response as TServerErrorResponse;

	let result = {
		error: typedResponse.error,
		message: ''
	};

	if (result.error) {
		const errorMessage = typedResponse?.message;

		result = {
			error: true,
			message: typeof errorMessage === 'string' ? errorMessage : 'Something went wrong'
		};
	}

	return result;
}

function checkSubmitQuestError<T>(response: T | TServerSubmitQuestErrorResponse): {
	error: boolean;
	message: string;
} {
	const typedResponse = response as TServerSubmitQuestErrorResponse;

	let errorMessage: string | undefined = typedResponse?.response?.data?.error?.details?.error;

	if (!errorMessage) {
		errorMessage = typedResponse?.message;
	}

	return {
		error: !!typedResponse?.response?.data?.error || !!typedResponse?.error,
		message: typeof errorMessage === 'string' ? errorMessage : 'Something went wrong'
	};
}

/**
 * Convert a singular word to its plural form (e.g. "box" to "boxes")
 * @param count count of item to check
 * @param value the singular value
 * @param customPlural custom plural value
 * @returns formatted string
 */
function isPlural(count: number | undefined, value: string, customPlural?: string): string {
	if (!count) {
		return value;
	}

	if (count > 1) {
		return customPlural ? customPlural : pluralize(value);
	}

	return value;
}

/**
 * Converts a value to a string and adds an optional suffix for numbers
 * @param value the value to convert
 * @param numberSuffix optional suffix for numbers, added after the value
 * @returns the converted value as a string, or "-" if the value is undefined
 */
function checkType(value: string | number | undefined, numberSuffix?: string, noneIfZero?: boolean): string {
	// If the value is a number, format it as a string and add the number suffix if provided
	if (typeof value === 'number') {
		if (noneIfZero && value === 0) {
			return 'None';
		}

		return `${value}${typeof numberSuffix === 'string' ? ` ${numberSuffix}` : ''}`;
	}

	// If the value is a string, return the string value or "-" if it is empty
	if (typeof value === 'string') {
		if (value === 'None' || value === '-') {
			return value;
		}

		const formattedValue = `${value}${typeof numberSuffix === 'string' ? ` ${numberSuffix}` : ''}`;
		return value ? formattedValue : '-';
	}

	// If the value is undefined, return "-"
	return '-';
}

/**
 * get the next or the previous campaign id based on the direction and the current campaign id
 * @param campaigns array of all campaigns
 * @param currentCampaignId current campaign id
 * @param direction direction to get the next or previous campaign id
 * @returns next or previous campaign id
 */
function getCampaignId(
	campaigns: ICampaign[],
	currentCampaignId: number | undefined,
	direction: 'next' | 'previous'
): number {
	if (!currentCampaignId) {
		return -1;
	}

	const currentCampaignIndex = campaigns.findIndex((campaign) => campaign.id === currentCampaignId);
	const nextCampaignIndex = direction === 'next' ? currentCampaignIndex + 1 : currentCampaignIndex - 1;
	const nextCampaign = campaigns[nextCampaignIndex];

	if (nextCampaign) {
		return nextCampaign.id;
	}

	return currentCampaignId;
}

function isDevNodeEnv(): boolean {
	return process.env.NODE_ENV === 'development';
}

function isDevEnv(): boolean {
	return process.env.BUILD_ENV === 'development';
}

function isSocketDebugNotificationsActivated(): boolean {
	// set to true if we want to show notification for each event
	return false && !isTestEnv() && (isDevNodeEnv() || isStagingEnv());
}

function isTestEnv(): boolean {
	return process.env.BUILD_ENV === 'test';
}

function isStagingEnv(): boolean {
	return process.env.BUILD_ENV === 'staging';
}

function isProdEnv(): boolean {
	return process.env.BUILD_ENV === 'production';
}

/**
 * wait for an element to be added to the DOM
 * @param selector the selector of the element
 * @returns
 * */
function waitForElm(selector: string) {
	return new Promise((resolve) => {
		if (document.querySelector(selector)) {
			return resolve(document.querySelector(selector));
		}

		const observer = new MutationObserver((mutations) => {
			if (document.querySelector(selector)) {
				resolve(document.querySelector(selector));
				observer.disconnect();
			}
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true
		});
	});
}

/**
 * generate a UUID
 * @returns
 */
function generateUUID() {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c == 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

/**
 * check if the user is on an overview page without query params
 * @param submenuId
 * @param href
 * @returns
 */
function isOverviewWithoutQueryParam(submenuId: ISubmenu['id'], href: string): boolean {
	let result = false;

	APP_LINKS_ARRAY.forEach((page: IAppLink) => {
		const pageHref = page.href as string;

		const firstSubmenuId = page.submenus?.[0]?.id;

		if (!result && firstSubmenuId === submenuId) {
			result = pageHref === href;
		}
	});

	return result;
}

/**
 * check if the user is on a subpage
 * @param submenuIds an array of submenu ids
 * @returns boolean
 */
function isOnSubpage(submenuIds: ISubmenu['id'][]): boolean {
	if (typeof window === 'undefined') return false;

	const submenuId = router?.query?.s as ISubmenu['id'];

	if (!submenuId) {
		return isOverviewWithoutQueryParam(submenuIds[0], router.pathname);
	}

	return submenuIds.includes(submenuId);
}

function getRequestState(isLoading: boolean, isError: boolean, isSuccess: boolean): ERequestState {
	if (isSuccess) {
		return ERequestState.success;
	}

	if (isLoading) {
		return ERequestState.loading;
	}

	if (isError) {
		return ERequestState.error;
	}

	return ERequestState.unset;
}

// create a function that will returns the right base url according to the BUILD_ENV environnement variable
function getBaseUrl(withProtocol = true) {
	let baseUrl = '';
	if (isProdEnv()) {
		baseUrl = 'https://nodeguardians.io';
	} else if (isStagingEnv()) {
		baseUrl = 'https://staging.nodeguardians.com';
	} else {
		baseUrl = 'http://localhost:3000';
	}

	return withProtocol ? baseUrl : baseUrl.replace(/^https?\:\/\//i, '');
}

// create a function that remove the protocol from a url
function removeProtocolFromUrl(url: string) {
	if (!url) return '';
	return url.replace(/^https?\:\/\//i, '');
}

function getNetwork(chainId: number): INetwork | undefined {
	return Object.values(NETWORKS_DATA).find(
		(network) => network.chainId === chainId || `0x0${network.chainId}` === chainId.toString()
	);
}

export {
	buildURL,
	capitalize,
	checkIfError,
	checkSubmitQuestError,
	checkType,
	formatRichText,
	generateUUID,
	getBaseUrl,
	getBrowser,
	getCampaignId,
	getCoords,
	getNetwork,
	getRequestState,
	goBackToDevhub,
	isDevEnv,
	isDevNodeEnv,
	isOnSubpage,
	isOverviewWithoutQueryParam,
	isPlural,
	isProdEnv,
	isSocketDebugNotificationsActivated,
	isStagingEnv,
	isTestEnv,
	jsonToObject,
	naturalSort,
	pushInternalURL,
	pushURL,
	removeDuplicates,
	removeProtocolFromUrl,
	removeQueryParams,
	updateQueryParams,
	waitForElm
};
